import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { Slot } from "radix-ui"

/**
 * Not shadcn's default button.
 *
 * The geometry and the palette are the studio's: `rounded-btn` is 0.25rem, because buttons
 * here are rectangles with a soft corner and never pills, and the colour pairs come off the
 * Northern Mint sheet rather than the library's neutral ramp. The press-scale and the
 * 0.3s cubic-bezier transition are carried over from the `.btn` CSS class this replaces, so
 * every button on the site still feels the same under the finger.
 *
 * `mono` is a studio addition with no shadcn equivalent: the uppercase wide-tracked
 * JetBrains Mono label used on section CTAs and carousel controls.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-btn text-sm font-semibold tracking-[0.01em] whitespace-nowrap outline-none transition-[background-color,border-color,color,transform] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-[0.97] focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-30 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Mint on ink, going pine-on-white at rest-to-hover. The site's primary action.
        default: "bg-brand-mint text-brand-ink hover:bg-brand-pine hover:text-white",
        destructive: "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/30",
        // A hairline box on the white ground that turns pine on hover.
        outline:
          "border border-brand-ink/20 bg-transparent text-brand-ink hover:border-brand-pine hover:text-brand-pine",
        secondary: "bg-brand-navy text-white hover:bg-brand-accent",
        ghost: "bg-transparent text-brand-ink hover:text-brand-accent",
        link: "text-brand-accent underline-offset-4 hover:underline",
        // The mono pill used on section CTAs. Full radius is deliberate here and only here.
        mono: "rounded-full bg-brand-navy px-5 font-mono text-[11px] font-normal uppercase tracking-widest text-white hover:bg-brand-accent",
      },
      size: {
        default: "h-11 px-5",
        sm: "h-9 gap-1.5 px-3",
        lg: "h-12 px-7",
        icon: "size-10",
        "icon-sm": "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
